import React, { useState, useEffect, useContext } from 'react';
import { Text } from '@fluentui/react';
import { CheckmarkCircle24Filled, Circle24Regular } from '@fluentui/react-icons';
import './styles/loading.css';
import './styles/global.css';
import blitzSessionContext from '../../../../context/blitzsession/blitzSessionContext';

const LoadingScreen = () => {
  const { setIsLoading, firstStudyFullyLoaded } = useContext(blitzSessionContext);
  const [currentQuoteIndex, setCurrentQuoteIndex] = useState(0);
  const [currentTaskIndex, setCurrentTaskIndex] = useState(0);
  const [isTaskCompleted, setIsTaskCompleted] = useState(false);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [allTasksCompleted, setAllTasksCompleted] = useState(false);

  const quotes = [
    "Precision in diagnosis saves lives.",
    "Every image tells a story waiting to be discovered.",
    "Excellence in radiology begins with attention to detail.",
    "Speed and accuracy - the hallmarks of modern reporting.",
    "Technology amplifies human expertise, never replaces it.",
    "In medical imaging, clarity of vision drives clarity of diagnosis.",
    "The future of radiology is intelligent, efficient, and precise."
  ];// add more quotes radiologist specific motivation, education, ethics, quote the author as well if it is well known quote 20-25 such

  const preparationTasks = [
    "Fetching latest case queue",
    "Optimizing user experience", 
    "Preparing reporting tools",
    "Configuring workspace preferences for you",
    "Initializing your AI partner",
    "Preparing your first case",
  ];

  useEffect(() => {
    const quoteInterval = setInterval(() => {
      setCurrentQuoteIndex((prev) => (prev + 1) % quotes.length);
    }, 3000);

    return () => {
      clearInterval(quoteInterval);
    };
  }, []);

  useEffect(() => {
    const taskInterval = setInterval(() => {
      setIsTaskCompleted(true);
      setTimeout(() => {
        setCurrentTaskIndex(prev => {
          if (prev < preparationTasks.length - 2) {
            setIsTaskCompleted(false);
            return prev + 1;
          } else if (prev === preparationTasks.length - 2) {
            setIsTaskCompleted(false);
            return prev + 1;
          }
          return prev;
        });
      }, 400);
    }, 800);

    if (currentTaskIndex >= preparationTasks.length - 1) {
      clearInterval(taskInterval);
    }

    return () => {
      clearInterval(taskInterval);
    };
  }, [currentTaskIndex]);

  useEffect(() => {
    if (firstStudyFullyLoaded && currentTaskIndex === preparationTasks.length - 1 && !allTasksCompleted) {
      console.log("First study fully loaded, completing loading screen");
      setIsTaskCompleted(true);
      setAllTasksCompleted(true);
      
      setTimeout(() => {
        setIsTransitioning(true);
        setTimeout(() => {
          setIsLoading(false);
        }, 1000);
      }, 500);
    }
  }, [firstStudyFullyLoaded, currentTaskIndex, allTasksCompleted, setIsLoading]);

  const currentTask = preparationTasks[currentTaskIndex];
  
  let progress;
  if (allTasksCompleted) {
    progress = 100;
  } else if (currentTaskIndex === preparationTasks.length - 1 && !isTaskCompleted) {
    progress = 95;
  } else {
    progress = ((currentTaskIndex + (isTaskCompleted ? 1 : 0)) / preparationTasks.length) * 100;
  }

  const completedTasksCount = allTasksCompleted 
    ? preparationTasks.length 
    : currentTaskIndex + (isTaskCompleted ? 1 : 0);

  return (
    <div className={`loading-screen ${isTransitioning ? 'transitioning' : ''}`}>
      <div className="loading-content">
        {/* Main Branding */}
        <div className="brand-section">
          <div className="logo-container">
            <div className="logo-icon">
              <svg width="40" height="40" viewBox="0 0 24 24" fill="currentColor">
                <path d="M11 21h-1l1-7H7.5c-.58 0-.57-.32-.38-.66.19-.34.05-.08.07-.12C8.48 10.94 10.42 7.54 13 3h1l-1 7h3.5c.49 0 .56.33.47.51l-.07.15C12.96 17.55 11 21 11 21z"/>
              </svg>
            </div>
          </div>
          <h1 className="main-title">Blitz Reporting</h1>
          <p className="subtitle">Engineered for Speed</p>
        </div>

        {/* Motivational Quotes */}
        <div className="quotes-section">
          <div className="quote-container">
            <Text size={500} className="quote-text">
              "{quotes[currentQuoteIndex]}"
            </Text>
          </div>
        </div>

        {/* Preparation Tasks */}
        <div className="tasks-section">
          <div className="single-task-container">
            <div className="task-icon">
              {(isTaskCompleted && currentTaskIndex < preparationTasks.length - 1) || allTasksCompleted ? (
                <CheckmarkCircle24Filled className="completed-icon" />
              ) : (
                <Circle24Regular className="pending-icon" />
              )}
            </div>
            <Text size={300} className={`task-text ${(isTaskCompleted && currentTaskIndex < preparationTasks.length - 1) || allTasksCompleted ? 'completed' : ''}`}>
              {currentTask}
            </Text>
          </div>
        </div>

        {/* Progress Bar */}
        <div className="progress-section">
          <div className="progress-bar">
            <div 
              className="progress-fill"
              style={{ 
                width: `${progress}%` 
              }}
            />
          </div>
          <Text size={200} className="progress-text">
            {Math.round(progress)}% Complete
            {/* {currentTaskIndex === preparationTasks.length - 1 && !firstStudyFullyLoaded && !allTasksCompleted && 
              <span style={{ marginLeft: '8px', fontSize: '12px', opacity: 0.7 }}>
                (Waiting for study images...)
              </span>
            } */}
          </Text>
        </div>
      </div>
    </div>
  );
};

export default LoadingScreen;